import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useProject } from '../../context/ProjectContext';
import { useToast } from '../../context/ToastContext';
import WordPressSettings from '../../lib/integrations/wordpress/components/WordPressSettings';
import FacebookSettings from '../../lib/integrations/facebook/components/FacebookSettings';
import GoogleAnalyticsSettings from '../../lib/integrations/google/components/GoogleAnalyticsSettings';
import { getSiteInfo, testConnection } from '../../lib/integrations/wordpress/api';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useConfirm } from '../../context/ConfirmContext';
import LinkedInSettings from '../../lib/integrations/linkedin/components/LinkedInSettings';
import InstagramSettings from '../../lib/integrations/instagram/components/InstagramSettings';

export default function ConnectionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { confirm } = useConfirm();
  const { currentProject } = useProject();
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConnection();
  }, [id]);

  const fetchConnection = async () => {
    try {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .eq('id', id)
        .eq('project_id', currentProject.id)
        .single();

      if (error) throw error;
      setConnection(data);
    } catch (error) {
      console.error('Error fetching connection:', error);
      addToast('Failed to load connection details', 'error');
      navigate('/connections');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Connection',
      message: 'Are you sure you want to delete this connection? This action cannot be undone.',
      confirmText: 'Delete',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        const { error } = await supabase
          .from('channels')
          .delete()
          .eq('id', connection.id);

        if (error) throw error;

        addToast('Connection deleted successfully', 'success');
        navigate('/connections');
      } catch (error) {
        console.error('Error deleting connection:', error);
        addToast('Failed to delete connection', 'error');
      }
    }
  };

  const handleUpdate = async (updates) => {
    try {
      if (updates.type === 'wordpress' && updates.settings?.siteUrl) {
        const siteInfo = await getSiteInfo(updates.settings.siteUrl);
        
        updates.settings = {
          ...updates.settings,
          pageImageUrl: siteInfo.faviconUrl,
          pageName: siteInfo.name
        };
      }

      const { error } = await supabase
        .from('channels')
        .update({
          name: updates.name,
          settings: updates.settings,
          credentials: updates.credentials,
          last_tested_at: updates.last_tested_at,
          is_configured: updates.is_configured,
          setup_required: updates.setup_required,
          updated_at: new Date().toISOString()
        })
        .eq('id', connection.id);

      if (error) throw error;

      setConnection(prev => ({
        ...prev,
        ...updates
      }));

      addToast('Connection updated successfully', 'success');
    } catch (error) {
      console.error('Error updating connection:', error);
      addToast('Failed to update connection', 'error');
    }
  };

  const handleTestConnection = async () => {
    if (!connection.credentials?.username || !connection.settings?.siteUrl) {
      addToast('Please enter all required WordPress credentials', 'error');
      return;
    }

    setTesting(true);
    try {
      const result = await testConnection({
        siteUrl: connection.settings.siteUrl,
        username: connection.credentials.username,
        applicationPassword: connection.credentials.applicationPassword
      });

      if (result.success) {
        await handleUpdate({
          ...connection,
          settings: {
            ...connection.settings,
            pageImageUrl: result.data.pageImageUrl,
            pageName: result.data.name
          },
          last_tested_at: new Date().toISOString(),
          is_configured: true,
          setup_required: false
        });
        
        addToast('Connection test successful!', 'success');
      } else {
        throw new Error(result.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      addToast(`Connection test failed: ${error.message}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/connections')}
              className="text-gray-400 hover:text-gray-300"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-semibold mb-2">{connection.name}</h1>
          </div>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
          >
            Delete Connection
          </button>
        </div>
        <p className="text-gray-400">Manage your connection settings</p>
      </div>

      <div className="bg-card border border-divider rounded-lg p-6">
        {connection.type === 'wordpress' && (
          <WordPressSettings connection={connection} onUpdate={handleUpdate} />
        )}
        
        {connection.type === 'facebook' && (
          <FacebookSettings connection={connection} onUpdate={handleUpdate} />
        )}

        {connection.type === 'google_analytics' && (
          <GoogleAnalyticsSettings connection={connection} onUpdate={handleUpdate} />
        )}

        {connection.type === 'linkedin_profile' && (
          <LinkedInSettings connection={connection} onUpdate={handleUpdate} />
        )}

        {connection.type === 'instagram_page' && (
          <InstagramSettings connection={connection} onUpdate={handleUpdate} />
        )}
      </div>
    </div>
  );
} 